import { PropsWithChildren, ReactNode, useId } from 'react';
import {
  RadioGroup,
  RadioGroupIndicator,
  RadioGroupItem,
} from '@radix-ui/react-radio-group';
import { LayoutGroup, motion } from 'motion/react';

import css from './styles.module.css';

export interface SegmentedControlOptionProps extends PropsWithChildren {
  value: string;
  prefix?: ReactNode;
}

export function SegmentedControlOption({
  children,
  value,
  prefix,
}: SegmentedControlOptionProps) {
  return (
    <RadioGroupItem className={css.option} value={value}>
      <RadioGroupIndicator asChild>
        <motion.span
          className={css.indicator}
          layoutId="segmented-control-indicator"
          transition={{ type: 'spring', visualDuration: 0.15, bounce: 0.1 }}
        />
      </RadioGroupIndicator>

      <span className={css.optionContent}>
        {prefix && (
          <span className={css.optionPrefix} aria-hidden>
            {prefix}
          </span>
        )}
        <span className={css.optionLabel}>{children}</span>
      </span>
    </RadioGroupItem>
  );
}

export interface SegmentedControlProps extends PropsWithChildren {
  defaultValue?: string;
  value?: string;
  onValueChange?(value: string): void;
  disabled?: boolean;
  size?: 'sm' | 'md';
  'data-testid'?: string;
}

function Root({
  children,
  defaultValue,
  value,
  onValueChange,
  disabled,
  size = 'md',
  'data-testid': testId,
}: SegmentedControlProps) {
  const id = useId();
  return (
    <LayoutGroup id={id}>
      <RadioGroup
        className={css.root}
        id={id}
        name={id}
        defaultValue={defaultValue}
        value={value}
        onValueChange={onValueChange}
        disabled={disabled}
        orientation="horizontal"
        loop
        data-size={size}
        data-testid={testId}
      >
        {children}
      </RadioGroup>
    </LayoutGroup>
  );
}

export const SegmentedControl = Object.assign(Root, {
  Option: SegmentedControlOption,
});
